import { Avatar, Center, Flex, Text, Spinner, IconButton, Button } from '@chakra-ui/react';
import { isDefined } from '@duke-hq/lib';
import { ChatMeta, getChatTypeText } from '@duke-hq/svc-support-web';
import { ChatItem } from '@duke-hq/ui/components/Chat/ChatItem';
import { t } from '@lingui/core/macro';
import { useCallback, useContext } from 'react';
import { ListProps, ListItems } from '@duke-hq/ui/components/ListDetail/ListDetail';
import { djs } from '@duke-hq/djs';
import { SupportContext } from './ctx';
import { PlusIcon } from 'lucide-react';
import { trpcTenant } from '../../trpcTenant';

export function ChatList(props: ListProps<ChatMeta, string>) {
  return (
    <Flex direction="column" h="100%" overflow="auto">
      <ChatListHeader />
      <ChatListItems {...props} />
    </Flex>
  );
}

export function SupportChatList(props: ListProps<ChatMeta, string>) {
  return (
    <Flex direction="column" alignSelf="stretch" w="full" h="full">
      <SupportChatListHeader />
      <ChatListItems {...props} />
    </Flex>
  );
}

export function ChatListItems(props: ListProps<ChatMeta, string>) {
  const {
    items,
    isCondensed,
    isFetchingNextPage,
    isFetchingPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isLoading,
    isError,
    actions: { previousPage, nextPage },
  } = props;

  return (
    <Flex
      direction="column"
      alignItems="flex-start"
      alignSelf="stretch"
      backgroundColor="bg.surface"
      height="100%"
      width="100%"
      overflow="scroll"
    >
      {isLoading ? (
        <Spinner ml="4" size="xs" />
      ) : (
        <ChatItems
          selectedId={props.selectedId}
          items={items}
          isCondensed={isCondensed}
          isFetchingNextPage={isFetchingNextPage}
          isFetchingPreviousPage={isFetchingPreviousPage}
          hasNextPage={!!hasNextPage}
          hasPreviousPage={!!hasPreviousPage}
          isLoading={!!isLoading}
          isError={!!isError}
        />
      )}

      <Flex justifyContent="space-between" width="100%">
        <Flex flexGrow={1} width="30%" justifyContent="stretch" alignSelf="stretch">
          {hasPreviousPage ? (
            <Button disabled={!hasPreviousPage} onClick={previousPage} variant="link" margin="4">
              {t`Previous`}
              {isFetchingPreviousPage ? <Spinner width="8px" height="8px" pl="2" /> : null}
            </Button>
          ) : null}
        </Flex>

        <Flex flexGrow={1} width="30%" justifyContent="end">
          {hasNextPage ? (
            <Button
              disabled={isFetchingNextPage}
              onClick={nextPage}
              variant="link"
              justifyContent="flex-end"
              alignSelf="stretch"
              margin="4"
            >
              {isFetchingNextPage ? <Spinner width="8px" height="8px" pr="2" /> : null}
              {t`Next`}
            </Button>
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  );
}

export function ChatItems({
  selectedId,
  items,
  // @ts-expect-error -- fix this
  isCondensed,
  // @ts-expect-error -- fix this
  isFetchingNextPage,
  // @ts-expect-error -- fix this
  isFetchingPreviousPage,
  // @ts-expect-error -- fix this
  hasNextPage,
  // @ts-expect-error -- fix this
  hasPreviousPage,
  isLoading,
  // @ts-expect-error -- fix this
  isError,
}: {
  selectedId?: string;
  items: ListItems<ChatMeta, string>;
  isCondensed: boolean;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  isLoading: boolean;
  isError: boolean;
}) {
  const { actions } = useContext(SupportContext);

  if (!isLoading) {
    if (!items || items.length === 0) {
      return <EmptyState />;
    }
  }

  return (
    <Flex
      direction="column"
      alignItems="flex-start"
      alignSelf="stretch"
      width="100%"
      flexGrow={1}
      overflowY="scroll"
    >
      {items.map((item) => {
        if (!item?.data?.userDrn || !item?.data?.recentUsers) {
          return null;
        }

        let avatars;
        let updatedAt = '';

        const users = [item.data.userDrn, item.data.recentUsers]
          // @ts-expect-error - existing error
          .map(actions.getUser)
          .filter(isDefined);

        // @ts-expect-error -- fix this
        const names: string[] = users.map((user) => user.displayName);

        if (item?.data?.recentUsers) {
          if (typeof item?.data?.modified === 'string') {
            updatedAt = djs(item?.data?.modified).fromNow();
          }

          avatars = users.filter(isDefined).map((user) => {
            return (
              <Avatar
                key={`${item.id}#${user.drn}`}
                size="sm"
                // @ts-expect-error -- fix this
                name={user.displayName}
                src={user?.avatar ?? undefined}
              />
            );
          });
        }

        const { chatType } = item?.data || {};
        let chatTypeText = chatType != null ? getChatTypeText(chatType) : '';
        if (item?.data?.isArchived) {
          chatTypeText = `${chatType} [Archived]`;
        } else if (item?.data?.status === 0) {
          chatTypeText = `${chatType} [Closed]`;
        }

        return (
          <ChatItem
            key={item.id}
            title={names.join(', ')}
            avatars={avatars}
            chatType={chatTypeText}
            updatedAt={updatedAt}
            message={item?.data?.mostRecentMessage ?? ''}
            onClick={item.selectItem}
            bg={selectedId === item.id ? 'bg.muted' : 'bg.surface'}
            w="full"
          />
        );
      })}
    </Flex>
  );
}

function ChatListHeader() {
  const createChat = trpcTenant.support.createGeneralSupportChat.useMutation();

  const handleCreateChat = useCallback(() => {
    createChat.mutate();
  }, [createChat]);

  return (
    <Flex
      alignItems="center"
      alignSelf="stretch"
      justifyContent="space-between"
      p="16px 16px 8px 16px"
    >
      <Text textStyle="2xl" fontWeight="semibold" textTransform="capitalize">
        {t`Chats`}
      </Text>
      <IconButton
        rounded="full"
        size="sm"
        variant="outline"
        colorScheme="gray"
        aria-label={t`create chat`}
        icon={<PlusIcon size={20} />}
        onClick={handleCreateChat}
      />
    </Flex>
  );
}

function SupportChatListHeader() {
  const { actions } = useContext(SupportContext);

  return (
    <Flex
      alignItems="center"
      alignSelf="stretch"
      justifyContent="space-between"
      p="16px 16px 8px 16px"
    >
      <Text textStyle="2xl" fontWeight="semibold" textTransform="capitalize">
        {t`Support Chats`}
      </Text>
      <IconButton
        rounded="full"
        size="sm"
        variant="outline"
        colorScheme="gray"
        aria-label={t`create support chat`}
        icon={<PlusIcon size={20} />}
        onClick={actions.createStudentContextChat}
      />
    </Flex>
  );
}

function EmptyState() {
  return <Center padding="4">{t`No support chats.`}</Center>;
}
